/* eslint-disable react-hooks/exhaustive-deps */
import React, { BaseSyntheticEvent, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonChip,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonToast,
  IonToolbar,
} from '@ionic/react'
import './Signatures.page.css'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteAllSignatureData,
  saveTempSignature,
  setSelectedSignatureType,
  setSignatureSection,
  setSignatureChips,
  loadSignatureData,
  deleteSignatureType,
  showConfirmationAlert,
  deleteAttachment,
  saveSignatures,
  setSectionTwoReasonIncapableOfSigning,
  setSectionTwoSelectedOptionIncapableOfSigning,
  setSectionTwoAuthorizedRepresentative,
  setSectionThreeReasonIncapableOfSigning,
  setSectionThreeSelectedOptionIncapableOfSigning,
  setWitnessPrintedName,
  setCrew,
  setReceivingFacilityTitle,
  setReceivingFacilityPrintedName,
  setRepresentativePrintedName,
  setRepresentativePhone,
  setRepresentativeAddress,
  setAcknowledgePrivacyNotice,
  saveSignaturesPcr,
  clearSignatureData,
  saveSignatureChipsData,
  setWitnessPhone,
  setWitnessAddress,
  setIsMark,
} from '../store/signatures.actions'
import { getSignaturesSelector } from '../selectors/getSignatures.selector'
import { getSignatureOptionsSelector } from '../../options/selectors'
import { closeCircleOutline, lockClosed } from 'ionicons/icons'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { IAlertState } from '../../../components/alert/store/alert.types'
import Logger from '../../../services/logger.service'
import { getUserSelector } from '../../user/selectors'
import { Link } from 'react-router-dom'
import MultisectionPicklist from '../../../components/multisectionPicklist/MultisectionPicklist.component'
import SignatureCanvasPad from '../signatureCanvas/SignatureCanvasPad'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../common/common.types'
import SInput from '../../../components/stringInput/StringInput.component'
import { SignaturesConstants } from '../store/signatures.constants'
import { useWindowSize } from '../../../hooks'
import { ISignatureChip, ISignatureToastType, ISignatureTypeOption } from '../store/signatures.types'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { getTimesSelector } from '../../times/selectors'
import { isTablet } from '../../../utilities'
import { hasStandardSignatureUnsavedData } from '../../../utilities/signature.util'
import ReactSignatureCanvas from 'react-signature-canvas'
import Validation from '../../../components/validation/Validation.component'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'

const Signatures: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { width } = useWindowSize({ debounceMs: 300 })
  const [isExpanded, setIsExpanded] = useState(false)
  const isTabletDevice = isTablet()

  const { serviceId, userId } = useSelector(getUserSelector)
  const { arriveReceivingTime } = useSelector(getTimesSelector)
  const {
    sectionOptions,
    signatureTypeOptions,
    signatureSecTwoPatientUnableToSignOptions,
    selectedCrewMembers,
    sectionTwoAuthorizedRepresentativeOptions,
  } = useSelector(getSignatureOptionsSelector)
  const signaturesState = useSelector(getSignaturesSelector)
  const {
    isMark,
    signatureSection,
    selectedSignatureType,
    acknowledgePrivacyNotice,
    chips,
    sectionTwoSelectedOptionIncapableOfSigning,
    sectionTwoReasonIncapableOfSigning,
    sectionTwoAuthorizedRepresentative,
    sectionThreeReasonIncapableOfSigning,
    sectionThreeSelectedOptionIncapableOfSigning,
    witnessSignature,
    witnessPrintedName,
    witnessPhone,
    witnessAddress,
    tempSignature,
    crew,
    receivingFacilityPrintedName,
    receivingFacilityRepTitle,
    representativePrintedName,
    representativePhone,
    representativeAddress,
  } = signaturesState

  const [isFocused, setIsFocused] = useState(false)
  const canvasRef: MutableRefObject<ReactSignatureCanvas | undefined> = useRef()

  const [showToast, setShowToast] = useState(false)
  const isSignatureCanvasVisible = acknowledgePrivacyNotice && selectedSignatureType && signatureSection ? true : false

  const [enableSave, setEnableSave] = useState(false)

  const signatureOptions = sectionOptions.map((sectionObj: IOption) => {
    const signatureObj = signatureTypeOptions.filter(
      (signatureObj: ISignatureTypeOption) =>
        signatureObj.section === sectionObj.id && signatureObj.name !== SignaturesConstants.AMBULANCE_CREW_SIGNATURE
    )
    return { ...sectionObj, values: signatureObj }
  })

  const sectionThreeWarningAlert: IAlertState = {
    header: '',
    message: t('signature.alert.sectionThreeWarning.message'),
  }

  const changeSectionWarningAlert: IAlertState = {
    header: t('signature.alert.changeSectionWarning.header'),
    message: t('signature.alert.changeSectionWarning.message'),
  }

  const textBoxValue =
    sectionTwoSelectedOptionIncapableOfSigning && sectionTwoSelectedOptionIncapableOfSigning.id === 5
      ? sectionTwoReasonIncapableOfSigning
      : ''
  const signatureThreetextBoxValue =
    sectionThreeSelectedOptionIncapableOfSigning && sectionThreeSelectedOptionIncapableOfSigning.id === 5
      ? sectionThreeReasonIncapableOfSigning
      : ''

  const clearSignatureAlert: IAlertState = {
    header: t('signature.alert.clearSection.header'),
    message: t('signature.alert.clearSection.message'),
  }

  const determineToastType = (): ISignatureToastType => {
    const isCrew = selectedSignatureType?.signatureType === SignaturesConstants.CREW
    const isReceiving = selectedSignatureType?.signatureType === SignaturesConstants.RECEIVING
    const isPatient = selectedSignatureType?.signatureType === SignaturesConstants.PATIENT
    const crewFilled = chips.some((chip) => {
      return chip?.option?.signatureType === SignaturesConstants.CREW
    })
    const receivingFilled = chips.some((chip) => {
      return chip?.option?.signatureType === SignaturesConstants.RECEIVING
    })
    const witnessFilled = chips.some((chip) => {
      return chip?.option?.signatureType === SignaturesConstants.WITNESS
    })

    if (isCrew && !receivingFilled) {
      return 'receiving-required'
    } else if (isReceiving && !crewFilled) {
      return 'crew-required'
    } else if (isPatient && !witnessFilled) {
      return 'witness-required'
    } else {
      return 'success'
    }
  }

  const saveSignature = () => {
    const toastType = determineToastType()
    const isWarning =
      toastType === 'crew-required' || toastType === 'receiving-required' || toastType === 'witness-required'
    const toastDuration = isWarning ? 4000 : 2000

    canvasRef.current?.getCanvas().toBlob(function (blob: Blob | null) {
      dispatch(saveSignatures(blob))
    })
    dispatch(saveTempSignature(''))
    dispatch(saveSignatureChipsData())
    clearCanvas()
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
    }, toastDuration)
  }

  const reasonIncapableOfSigningForSecThreeOnChange = (option: IOption) => {
    if (option === null) {
      dispatch(setSectionThreeSelectedOptionIncapableOfSigning(option))
      dispatch(setSectionThreeReasonIncapableOfSigning(''))
      return
    }
    if (option.id !== sectionThreeSelectedOptionIncapableOfSigning?.id) {
      dispatch(setSectionThreeSelectedOptionIncapableOfSigning(option))
      if (option.id !== 5) {
        dispatch(setSectionThreeReasonIncapableOfSigning(option.name))
      } else {
        dispatch(setSectionThreeReasonIncapableOfSigning(''))
      }
      dispatch(saveSignaturesPcr())
    }
  }

  const authorizedRepresentativeForSecTwoOnChange = (option: IOption) => {
    dispatch(setSectionTwoAuthorizedRepresentative(option))
    dispatch(saveSignaturesPcr())
  }

  const setSignatureThreeTextBoxValue = (e: CustomEvent) => {
    e.preventDefault()
    e.detail.value.length > 100
      ? dispatch(setSectionThreeReasonIncapableOfSigning(e.detail.value.slice(0, 100)))
      : dispatch(setSectionThreeReasonIncapableOfSigning(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setCrewOnChange = (option: IOption) => {
    dispatch(setCrew(option))
    dispatch(saveSignaturesPcr())
  }

  const checkSelectedCrewMember = useMemo(() => {
    if (crew) {
      const crewMemberByIndex = selectedCrewMembers.findIndex((option) => option.id === crew.id)
      if (crewMemberByIndex === -1) {
        return true
      }
      return false
    }
  }, [crew, selectedCrewMembers])

  const setReceivingFacilityTitleOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setReceivingFacilityTitle(e.detail.value))
  }

  const setReceivingFacilityPrintedNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setReceivingFacilityPrintedName(e.detail.value))
  }

  const onClearButtonClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const alertState = {
      ...clearSignatureAlert,
      buttons: [{ text: 'No' }, { text: 'Yes', role: 'destructive', handler: handleClear }],
    }
    dispatch(showAlert(alertState))
  }

  const handleClear = () => {
    Logger.info(`Signatures section: ${signatureSection?.id} cleared by user: ${userId} in service: ${serviceId}`)
    clearCanvas()
    dispatch(setSignatureChips([]))
    dispatch(deleteAllSignatureData())
    dispatch(saveTempSignature(''))
  }

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const setSelectedOption = (selectedOption: ISignatureTypeOption) => {
    const currentSelectedChip = chips.find((chipObj) => {
      return chipObj.option?.id === selectedOption?.id
    })
    if (currentSelectedChip && !hasStandardSignatureUnsavedData(signaturesState)) {
      loadData(currentSelectedChip)
    } else {
      if (hasStandardSignatureUnsavedData(signaturesState)) {
        const alertState = {
          ...changeSectionWarningAlert,
          buttons: [
            {
              text: 'No',
            },
            {
              text: 'Yes',
              role: 'destructive',
              handler: () => {
                dispatch(clearSignatureData())
                setSelectedSignature(selectedOption)
                if (currentSelectedChip) {
                  loadData(currentSelectedChip)
                }
                setTimeout(() => {
                  showSectionThreeAlert(selectedOption.section)
                }, 1000)
              },
            },
          ],
        }
        dispatch(showAlert(alertState))
      } else {
        setSelectedSignature(selectedOption)
        showSectionThreeAlert(selectedOption.section)
      }
    }
  }

  const setSelectedSection = (selectedSection: IOption) => {
    if (signatureSection && signatureSection?.name !== selectedSection?.name) {
      dispatch(setAcknowledgePrivacyNotice(false))
    }
    dispatch(setSignatureSection(selectedSection))
  }

  const showSectionThreeAlert = (sectionId: number) => {
    if (
      sectionId === 3 &&
      selectedSignatureType?.signatureType !== SignaturesConstants.CREW &&
      selectedSignatureType?.signatureType !== SignaturesConstants.RECEIVING &&
      chips.length === 0
    ) {
      if (!arriveReceivingTime) {
        dispatch(showAlert(sectionThreeWarningAlert))
      }
    }
  }

  const setSelectedSignature = (selectedOption: ISignatureTypeOption) => {
    const selectedSection = sectionOptions.find((section: IOption) => section.id === selectedOption?.section)
    if (selectedSection) setSelectedSection(selectedSection)
    dispatch(setSelectedSignatureType(selectedOption))
    clearCanvas()
    dispatch(loadSignatureData(null))
    dispatch(saveTempSignature(''))
  }

  const clearCanvas = () => {
    canvasRef.current && canvasRef.current.clear()
  }

  const handleRemove = (signChip: ISignatureChip) => {
    dispatch(deleteSignatureType(signChip.option))
    const chipsArr = [...chips]
    chipsArr.length === 1 && dispatch(setAcknowledgePrivacyNotice(false))
    const chipDataIndex = chipsArr.findIndex((chipObject) => chipObject.option?.id === signChip.option?.id)
    if (chipDataIndex >= 0) {
      chipsArr.splice(chipDataIndex, 1)
      dispatch(setSignatureChips(chipsArr))
    }
    dispatch(deleteAttachment())
    dispatch(saveTempSignature(''))
    dispatch(saveSignatureChipsData())
  }

  const removeSignatureChip = (signChip: ISignatureChip) => {
    const removeSigAlert: IAlertState = {
      header: t('signature.alert.deleteChip.header'),
      message: t('signature.alert.deleteChip.message'),
      buttons: [{ text: 'No' }, { text: 'Yes', role: 'destructive', handler: () => handleRemove(signChip) }],
    }
    dispatch(showAlert(removeSigAlert))
    const signData = signaturesState.attachments.find((item) => {
      return signChip.option?.signatureType && item.key.includes(signChip.option?.signatureType)
    })
    dispatch(showConfirmationAlert(signData?.key, 'signature'))
  }

  const reasonIncapableOfSigningOnChange = (option: IOption) => {
    if (option === null) {
      dispatch(setSectionTwoSelectedOptionIncapableOfSigning(option))
      dispatch(setSectionTwoReasonIncapableOfSigning(''))
      return
    }
    if (option.id !== sectionTwoSelectedOptionIncapableOfSigning?.id) {
      dispatch(setSectionTwoSelectedOptionIncapableOfSigning(option))
      if (option.id !== 5) {
        dispatch(setSectionTwoReasonIncapableOfSigning(option.name))
      } else {
        dispatch(setSectionTwoReasonIncapableOfSigning(''))
      }
      dispatch(saveSignaturesPcr())
    }
  }

  const setTextBoxValue = (e: CustomEvent) => {
    e.preventDefault()
    e.detail.value.length > 100
      ? dispatch(setSectionTwoReasonIncapableOfSigning(e.detail.value.slice(0, 100)))
      : dispatch(setSectionTwoReasonIncapableOfSigning(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setWitnessPrintedNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWitnessPrintedName(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setWitnessPhoneOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWitnessPhone(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setWitnessAddressOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setWitnessAddress(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setRepresentativePhoneOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setRepresentativePhone(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const setRepresentativeAddressOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setRepresentativeAddress(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const showDeleteWitnessSignatureConfirmationAlert = (checkedValue: number | null) => {
    const removeWitnessSignAlert: IAlertState = {
      header: t('signature.alert.deleteWitnessSignature.header'),
      message: t('signature.alert.deleteWitnessSignature.message'),
      buttons: [
        {
          text: 'No',
          handler: () => {
            dispatch(setIsMark(1))
            dispatch(saveSignaturesPcr())
          },
        },
        {
          text: 'Yes',
          role: 'destructive',
          handler: () => handleRemoveWitnessSign(checkedValue),
        },
      ],
    }
    dispatch(showAlert(removeWitnessSignAlert))
  }

  const setIsMarkOnChange = (e: CustomEvent) => {
    e.preventDefault()
    const checked = e?.detail?.checked
    const checkedValue: number | null = checked ? 1 : null

    if (checkedValue !== 1 && witnessSignature) {
      showDeleteWitnessSignatureConfirmationAlert(checkedValue)
    } else {
      dispatch(setIsMark(checkedValue))
      dispatch(saveSignaturesPcr())
    }
  }

  const handleRemoveWitnessSign = (checked: any) => {
    const witnessChip = chips.find((chip) => chip.option?.signatureType === SignaturesConstants.WITNESS)
    if (witnessChip) {
      const signData = signaturesState.attachments.find((item) => {
        return witnessChip.option?.signatureType && item.key.includes(witnessChip.option?.signatureType)
      })
      dispatch(showConfirmationAlert(signData?.key, 'signature'))
      handleRemove(witnessChip)
    }
  }

  const chooseSignatureType = !selectedSignatureType && chips.length === 0 ? t('signature.chooseSignatureType') : ''
  const selectSectionSignType =
    !selectedSignatureType && !signatureSection && chips.length > 0 ? `Select ${chips[0]?.name} Signature Type` : ''

  const canvasContent = {
    canvasData: tempSignature,
    canvasHeader: selectedSignatureType ? selectedSignatureType.signatureType + ' SIGNATURE' : '',
    canvasLink: !acknowledgePrivacyNotice && selectedSignatureType ? AppRouterConstants.TERMS_CONDITIONS_TAB : '',
    icon: lockClosed,
    canvasLabel: chooseSignatureType || selectSectionSignType,
  }

  const loadCanvas = (blob: string) => {
    if (canvasRef.current) {
      const canvasWidth = canvasRef?.current?.getCanvas().width
      const canvasHeight = canvasRef?.current?.getCanvas().height
      clearCanvas()
      canvasRef.current.fromDataURL(blob, {
        width: canvasWidth,
        height: canvasHeight,
      })
    }
  }

  const loadData = (currentSelectedChip: ISignatureChip) => {
    dispatch(loadSignatureData(currentSelectedChip))
  }

  useEffect(() => {
    if (selectedSignatureType) {
      const currentSelectedChip = chips.find((chip) => {
        return chip.option?.name === selectedSignatureType.name
      })
      currentSelectedChip && loadData(currentSelectedChip)
    }
  }, [])

  useEffect(() => {
    if (tempSignature) {
      loadCanvas(tempSignature)
    }
  }, [width, tempSignature, isExpanded])

  const updateSaveButton = () => {
    const isCrew = selectedSignatureType?.signatureType === SignaturesConstants.CREW
    const isReceiving = selectedSignatureType?.signatureType === SignaturesConstants.RECEIVING
    const isWitness = selectedSignatureType?.signatureType === SignaturesConstants.WITNESS
    const issRepresentative = selectedSignatureType?.signatureType === SignaturesConstants.REPRESENTATIVE
    const isSigned = !!tempSignature

    if (isCrew) {
      const shouldEnable =
        isSigned && !!selectedSignatureType && !!sectionThreeSelectedOptionIncapableOfSigning && !!crew
      setEnableSave(shouldEnable)
      return
    }

    if (isWitness) {
      const shouldEnable = isSigned && isMark === 1 && !!witnessPrintedName
      setEnableSave(shouldEnable)
      return
    }

    if (issRepresentative) {
      const shouldEnable =
        isSigned && !!sectionTwoReasonIncapableOfSigning && !!representativePrintedName && !!representativeAddress
      setEnableSave(shouldEnable)
      return
    }

    if (isReceiving) {
      const shouldEnable =
        isSigned &&
        !!selectedSignatureType &&
        !!sectionThreeSelectedOptionIncapableOfSigning &&
        !!receivingFacilityPrintedName &&
        !!receivingFacilityRepTitle
      setEnableSave(shouldEnable)
      return
    }

    setEnableSave(isSigned)
  }

  useEffect(() => {
    updateSaveButton()
  }, [
    selectedSignatureType,
    tempSignature,
    sectionThreeSelectedOptionIncapableOfSigning,
    sectionTwoReasonIncapableOfSigning,
    crew,
    receivingFacilityPrintedName,
    receivingFacilityRepTitle,
    isMark,
    witnessPrintedName,
    sectionTwoReasonIncapableOfSigning,
    representativePrintedName,
    representativeAddress,
    witnessAddress,
    witnessPhone,
  ])

  const onDrawEnd = () => {
    if (canvasRef.current) {
      const base64 = canvasRef.current.getCanvas().toDataURL()
      dispatch(saveTempSignature(base64))
    }
    const ele = document.activeElement as HTMLElement
    if (ele) ele?.blur()
  }

  const setRepresentativePrintedNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setRepresentativePrintedName(e.detail.value))
    dispatch(saveSignaturesPcr())
  }

  const renderToast = () => {
    const toastType = determineToastType()
    const isWarning =
      toastType === 'crew-required' ||
      toastType === 'receiving-required' ||
      (toastType === 'witness-required' && isMark)

    const toastDuration = isWarning ? 4000 : 2000

    let toastMessage = t('signature.toast.success')
    if (toastType === 'receiving-required') toastMessage = t('signature.toast.receivingRequired')
    if (toastType === 'crew-required') toastMessage = t('signature.toast.crewRequired')
    if (toastType === 'witness-required' && isMark) toastMessage = t('signature.toast.witnessRequired')

    return (
      <IonToast
        isOpen={showToast}
        position='top'
        {...getToastProps(isWarning ? ToastColor.Warning : ToastColor.Success)}
        message={toastMessage}
        duration={toastDuration}
        onDidDismiss={() => setShowToast(false)}
      />
    )
  }

  const renderContent = () => {
    return (
      <>
        <IonGrid className='no-padding'>
          <IonRow>
            <IonCol className='picklist'>
              <MultisectionPicklist
                dataTestID='SignatureSection'
                listHeader={t('signature.multisectionPicklistHeader')}
                label={chips.length > 0 ? `Add Another ${chips[0]?.name} Signature` : 'Signature Type'}
                options={signatureOptions}
                currentSelectedOption={selectedSignatureType}
                setSelectedOption={setSelectedOption}
                selectedOptions={chips}
              />
              <hr className='field-divider' />
            </IonCol>
          </IonRow>
        </IonGrid>
        {signatureSection?.id === 2 && (
          <>
            <IonRow>
              <IonCol className='picklist picklist-section'>
                <SingleSelect
                  dataTestID='SignatureSectionTwo'
                  options={signatureSecTwoPatientUnableToSignOptions}
                  selectedOption={sectionTwoSelectedOptionIncapableOfSigning}
                  listHeader={t('signature.reasonForNotSigningHeader')}
                  label={t('signature.reasonForNotSigningLabel')}
                  action={reasonIncapableOfSigningOnChange}
                  customInput={{
                    textboxEnabledId: 5,
                    textboxSetAction: setTextBoxValue,
                    textboxValue: textBoxValue,
                    setTextBoxActionName: SignaturesConstants.SET_SECTION_TWO_REASON_INCAPABLE_OF_SIGNING,
                    maxlength: 100,
                    action: setSectionTwoReasonIncapableOfSigning,
                  }}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='SignatureSectionTwoAuthorizedRepresentative'
                  options={sectionTwoAuthorizedRepresentativeOptions}
                  selectedOption={sectionTwoAuthorizedRepresentative}
                  listHeader={t('signature.authorizedRepresentative')}
                  label={t('signature.authorizedRepresentative')}
                  action={authorizedRepresentativeForSecTwoOnChange}
                />
              </IonCol>
            </IonRow>
          </>
        )}

        {signatureSection?.id === 3 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='SignatureSectionThree'
                  options={signatureSecTwoPatientUnableToSignOptions}
                  selectedOption={sectionThreeSelectedOptionIncapableOfSigning}
                  listHeader={t('signature.reasonForNotSigningHeader')}
                  label={t('signature.reasonForNotSigningLabel')}
                  action={reasonIncapableOfSigningForSecThreeOnChange}
                  customInput={{
                    textboxEnabledId: 5,
                    textboxSetAction: setSignatureThreeTextBoxValue,
                    textboxValue: signatureThreetextBoxValue,
                    setTextBoxActionName: SignaturesConstants.SET_SECTION_THREE_REASON_INCAPABLE_OF_SIGNING,
                    maxlength: 100,
                    action: setSectionThreeReasonIncapableOfSigning,
                  }}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        <SignatureCanvasPad
          canvasRef={canvasRef}
          isCanvasVisible={isSignatureCanvasVisible}
          canvasContent={canvasContent}
          onClear={() => {
            dispatch(saveTempSignature())
          }}
          onEnd={onDrawEnd}
          canvasHeight={215}
          canvasWidth={width}
          isCanvasExpandable={true}
          onCanvasExpandChange={(isExpanded) => {
            setIsExpanded(isExpanded)
          }}
        />

        {selectedSignatureType?.signatureType === SignaturesConstants.PATIENT && (
          <div className='checkbox-label-container'>
            <IonCheckbox
              onIonChange={setIsMarkOnChange}
              slot='start'
              data-label='isMark'
              data-testid='isMark'
              checked={isMark === 1}
            />
            <IonLabel className='checkbox-label'>{t('signature.checkMarkText')}</IonLabel>
          </div>
        )}

        {selectedSignatureType?.signatureType === SignaturesConstants.CREW && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  isPHI
                  dataTestID='CrewMembers'
                  options={selectedCrewMembers}
                  selectedOption={crew}
                  listHeader={t('signature.crewMember')}
                  label={t('signature.crewMember')}
                  action={setCrewOnChange}
                />
                {checkSelectedCrewMember && (
                  <IonLabel color='danger'>{t('signature.selectedCremMemberError')}</IonLabel>
                )}
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
        {selectedSignatureType?.signatureType === SignaturesConstants.RECEIVING && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  label={t('signature.printedNameReceivingFacilityRepresentative')}
                  dataTestID='PrintedNameReceivingFacilityRepresentative'
                  inputValue={receivingFacilityPrintedName}
                  onChangeAction={setReceivingFacilityPrintedNameOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  label={t('signature.printedTitleReceivingFacilityRepresentative')}
                  dataTestID='PrintedTitleReceivingFacilityRepresentative'
                  inputValue={receivingFacilityRepTitle}
                  onChangeAction={setReceivingFacilityTitleOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        {selectedSignatureType?.signatureType === SignaturesConstants.REPRESENTATIVE && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='PrintedName'
                  label={t('signature.printedName')}
                  inputValue={representativePrintedName}
                  onChangeAction={setRepresentativePrintedNameOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_REPRESENTATIVE_PRINTED_NAME} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='Phone'
                  label={t('signature.phone')}
                  inputValue={representativePhone}
                  onChangeAction={setRepresentativePhoneOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_REPRESENTATIVE_PHONE} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='Address'
                  label={t('signature.address')}
                  inputValue={representativeAddress}
                  onChangeAction={setRepresentativeAddressOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_REPRESENTATIVE_ADDRESS} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='landscape-signature-action'>{renderFooterButtons()}</IonCol>
            </IonRow>
          </>
        )}
        {selectedSignatureType?.signatureType === SignaturesConstants.WITNESS && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='PrintedName'
                  label={t('signature.printedName')}
                  inputValue={witnessPrintedName}
                  onChangeAction={setWitnessPrintedNameOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_WITNESS_PRINTED_NAME} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='Phone'
                  label={t('signature.phone')}
                  inputValue={witnessPhone}
                  onChangeAction={setWitnessPhoneOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_WITNESS_PHONE} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  dataTestID='Address'
                  label={t('signature.address')}
                  inputValue={witnessAddress}
                  onChangeAction={setWitnessAddressOnChange}
                  onFocusAction={onFocus}
                  onBlurAction={onBlur}
                />
                <Validation id={SignaturesConstants.SET_WITNESS_ADDRESS} />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className='landscape-signature-action'>{renderFooterButtons()}</IonCol>
            </IonRow>
          </>
        )}
        {isFocused && (
          <IonItemDivider
            color='light'
            className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'}
          />
        )}
      </>
    )
  }

  const renderSignatureChip = (signatureChip: ISignatureChip) => {
    const name = signatureChip.option?.name && signatureChip.option?.name.split(' ')[0]

    return (
      <IonChip
        key={signatureChip?.option?.name}
        onClick={() => {
          loadData(signatureChip)
        }}
        className='signature-chip'>
        <IonLabel className=''>{name}</IonLabel>
        <IonIcon
          class='ion-align-self-center'
          size='small'
          icon={closeCircleOutline}
          color='white'
          onClick={() => {
            removeSignatureChip(signatureChip)
          }}
        />
      </IonChip>
    )
  }

  const renderFooterButtons = () => {
    return (
      <IonButtons className='ion-padding'>
        <IonButton
          className='btn-signature signature-button mr-8'
          color='danger'
          fill='outline'
          data-testid='ClearButton'
          onClick={onClearButtonClick}
          disabled={chips.length > 0 ? false : true}>
          {t('signature.clear')}
        </IonButton>
        <IonButton
          className='btn-signature signature-button btn-primary'
          data-testid='SaveSignatureButton'
          disabled={!enableSave}
          onClick={saveSignature}>
          {t('signature.saveSignature')}
        </IonButton>
      </IonButtons>
    )
  }

  const renderFooter = () => {
    return (
      <IonFooter className='signature-footer'>
        <IonToolbar color='light'>
          {chips.length > 0 && (
            <IonGrid className='padding-bottom'>
              <IonRow className='chip-heading'>Saved {chips[0]?.name} Signatures</IonRow>
              <IonRow className='ion-nowrap no-flex-shrink'>
                {chips.map((signatureChip: ISignatureChip, index) => {
                  return <React.Fragment key={index}>{renderSignatureChip(signatureChip)}</React.Fragment>
                })}
              </IonRow>
            </IonGrid>
          )}
          {acknowledgePrivacyNotice && (
            <IonRow className='ion-nowrap' color=''>
              <IonItem color='light' className='terms-checkbox-item'>
                <IonCheckbox
                  color='light'
                  disabled={true}
                  className='terms-checkbox'
                  slot='start'
                  checked={acknowledgePrivacyNotice}></IonCheckbox>
              </IonItem>
              <IonLabel className='terms-text'>
                {t('signature.signatoryAgrees')}
                <Link className='text-link' to={AppRouterConstants.TERMS_CONDITIONS_TAB}>
                  {t('signature.termsAndConditionsLowercase')}
                </Link>
              </IonLabel>
            </IonRow>
          )}
          {renderFooterButtons()}
        </IonToolbar>
      </IonFooter>
    )
  }

  return (
    <>
      {showToast && <>{renderToast()}</>}
      <hr className='field-divider' />
      <IonContent fullscreen>
        <IonList className={`${isExpanded && 'h-100'}`}>{renderContent()}</IonList>
      </IonContent>
      {renderFooter()}
    </>
  )
}

export default Signatures
